import React from 'react';
import {
  UserGroupIcon,
  ShoppingBagIcon,
  ChartBarIcon,
} from '@heroicons/react/24/solid';

export interface Activity {
  id: string;
  type: 'new_racer' | 'product_purchase' | 'race_result' | 'profile_update' | 'system_event';
  title: string;
  description: string;
  timestamp: string;
  metadata?: {
    racerId?: string;
    racerName?: string;
    productId?: string;
    productName?: string;
    raceId?: string;
    raceName?: string;
  };
}

const getActivityIcon = (type: Activity['type']) => {
  switch (type) {
    case 'new_racer':
    case 'profile_update':
      return UserGroupIcon;
    case 'product_purchase':
      return ShoppingBagIcon;
    case 'race_result':
    case 'system_event':
    default:
      return ChartBarIcon;
  }
};

const getActivityColor = (type: Activity['type']) => {
  switch (type) {
    case 'new_racer':
      return 'bg-green-900/50 text-green-300';
    case 'product_purchase':
      return 'bg-blue-900/50 text-blue-300';
    case 'race_result':
      return 'bg-purple-900/50 text-purple-300';
    case 'profile_update':
      return 'bg-yellow-900/50 text-yellow-300';
    case 'system_event':
      return 'bg-gray-900/50 text-gray-300';
    default:
      return 'bg-gray-900/50 text-gray-300';
  }
};

interface ActivityFeedProps {
  activities: Activity[];
  isLoading?: boolean;
  error?: string;
}

export const ActivityFeed: React.FC<ActivityFeedProps> = ({ activities, isLoading, error }) => {
  if (isLoading) {
    return (
      <div className="animate-pulse">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex space-x-4 mb-4">
            <div className="w-10 h-10 bg-black/30 rounded-full"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-black/30 rounded w-3/4"></div>
              <div className="h-3 bg-black/30 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-4">
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  if (!activities.length) {
    return (
      <div className="text-center py-4">
        <p className="text-gray-400">No recent activity</p>
      </div>
    );
  }

  return (
    <div className="flow-root">
      <ul className="-mb-8">
        {activities.map((activity, index) => {
          const Icon = getActivityIcon(activity.type);
          const colorClasses = getActivityColor(activity.type);

          return (
            <li key={activity.id}>
              <div className="relative pb-8">
                {index < activities.length - 1 && (
                  <span
                    className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-teal-500/20"
                    aria-hidden="true"
                  />
                )}
                <div className="relative flex space-x-3">
                  <div>
                    <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-black/30 ${colorClasses}`}>
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                  </div>
                  <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                    <div>
                      <p className="text-sm text-gray-200">{activity.title}</p>
                      <p className="text-sm text-gray-400">{activity.description}</p>
                    </div>
                    <div className="whitespace-nowrap text-right text-sm text-gray-400">
                      <time dateTime={activity.timestamp}>
                        {new Date(activity.timestamp).toLocaleString()}
                      </time>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}; 